import { PRODUCT_CATEGORY_LABELS, SUPPORT_LOG_EXTENSIONS } from './constants';
import type { ProductCategory } from './types';

/** "Effect", or "Effect + App" for products that are more than one thing. */
export function categoryLabel(categories: readonly ProductCategory[]): string {
  return categories.map((c) => PRODUCT_CATEGORY_LABELS[c] ?? c).join(' + ');
}

export function formatMoney(cents: number, currency = 'USD', locale?: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

export function effectivePriceCents(p: { priceCents: number; salePriceCents: number | null }): number {
  return p.salePriceCents != null && p.salePriceCents < p.priceCents ? p.salePriceCents : p.priceCents;
}

export function slugify(input: string): string {
  return input
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

const YT_ID = /^[A-Za-z0-9_-]{11}$/;

/** Accepts a bare video id or any common YouTube URL form and returns the 11-char id. */
export function parseYouTubeId(input: string): string | null {
  const s = input.trim();
  if (YT_ID.test(s)) return s;
  try {
    const url = new URL(s);
    const host = url.hostname.replace(/^www\.|^m\./, '');
    if (host === 'youtu.be') {
      const id = url.pathname.slice(1).split('/')[0];
      return YT_ID.test(id) ? id : null;
    }
    if (host === 'youtube.com' || host === 'youtube-nocookie.com' || host === 'music.youtube.com') {
      const v = url.searchParams.get('v');
      if (v && YT_ID.test(v)) return v;
      const m = url.pathname.match(/^\/(?:embed|shorts|live|v)\/([A-Za-z0-9_-]{11})/);
      if (m) return m[1];
    }
  } catch {
    /* not a URL */
  }
  return null;
}

/** Redeem codes are case-insensitive and may be typed with or without dashes/spaces. */
export function normalizeCode(input: string): string {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** Formats a normalized code as XXXX-XXXX-XXXX-XXXX for display. */
export function formatCode(normalized: string): string {
  return normalized.replace(/(.{4})(?=.)/g, '$1-');
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

/** Semantic-ish version compare: "1.10.0" > "1.9.2". Non-numeric parts compare as strings. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split(/[.+-]/);
  const pb = b.split(/[.+-]/);
  const n = Math.max(pa.length, pb.length);
  for (let i = 0; i < n; i++) {
    const x = pa[i] ?? '0';
    const y = pb[i] ?? '0';
    const nx = Number(x);
    const ny = Number(y);
    if (!Number.isNaN(nx) && !Number.isNaN(ny)) {
      if (nx !== ny) return nx < ny ? -1 : 1;
    } else if (x !== y) {
      return x < y ? -1 : 1;
    }
  }
  return 0;
}

/** True for the file names support accepts as logs (.txt, .log; any case). */
export function isAllowedLogName(name: string): boolean {
  const lower = name.trim().toLowerCase();
  return SUPPORT_LOG_EXTENSIONS.some((ext) => lower.endsWith(ext) && lower.length > ext.length);
}

/**
 * True when decoded file contents are readable text, not a binary file renamed to .txt: no NUL
 * characters, and almost no other control characters or undecodable bytes (U+FFFD). Tabs, line
 * breaks, form feeds and ANSI colour codes are fine; a real log has at most a handful of oddities.
 */
export function looksLikeText(text: string): boolean {
  if (text.includes('\u0000')) return false;
  let odd = 0;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if ((c < 32 && c !== 9 && c !== 10 && c !== 12 && c !== 13 && c !== 27) || c === 127 || c === 0xfffd) odd++;
  }
  return odd <= Math.max(8, text.length / 1000);
}

/** One picture, clip or video in a product page's media viewer. */
export type ProductMediaItem =
  | { key: string; role: 'hero' | 'thumbnail' | 'gallery'; kind: 'image' | 'video'; src: string }
  | { key: string; role: 'youtube'; kind: 'youtube'; id: string };

export const isVideoUrl = (url: string): boolean => /\.(mp4|webm)(\?|$)/i.test(url);

/**
 * A product page's media, in the order the viewer shows them (the first one opens first).
 *
 * The default order is hero image, thumbnail, gallery (upload order), then YouTube videos. The
 * admin can drag them into another order, saved as `mediaOrder` keys ("hero", "thumbnail",
 * "gallery:<url>", "youtube:<id>"). Media missing from `mediaOrder` (added later, or a product
 * never reordered) follow the ordered ones, in the default order. The store card's thumbnail
 * and hover loop don't depend on this.
 */
export function productMedia(p: {
  heroUrl: string | null;
  thumbnailUrl: string | null;
  gallery: readonly string[];
  youtubeIds: readonly string[];
  mediaOrder?: readonly string[];
}): ProductMediaItem[] {
  const list: ProductMediaItem[] = [];
  if (p.heroUrl) list.push({ key: 'hero', role: 'hero', kind: isVideoUrl(p.heroUrl) ? 'video' : 'image', src: p.heroUrl });
  if (p.thumbnailUrl) list.push({ key: 'thumbnail', role: 'thumbnail', kind: isVideoUrl(p.thumbnailUrl) ? 'video' : 'image', src: p.thumbnailUrl });
  for (const g of p.gallery) list.push({ key: `gallery:${g}`, role: 'gallery', kind: isVideoUrl(g) ? 'video' : 'image', src: g });
  for (const id of p.youtubeIds) list.push({ key: `youtube:${id}`, role: 'youtube', kind: 'youtube', id });
  const order = p.mediaOrder ?? [];
  if (order.length === 0) return list;
  const rank = new Map<string, number>();
  order.forEach((k, i) => {
    if (!rank.has(k)) rank.set(k, i);
  });
  // Array.prototype.sort is stable: equal ranks (unordered media, or a picture used twice)
  // keep their default order.
  return list
    .map((item, i) => ({ item, i, r: rank.get(item.key) ?? Number.MAX_SAFE_INTEGER }))
    .sort((a, b) => a.r - b.r || a.i - b.i)
    .map((x) => x.item);
}
